import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Send } from "lucide-react";
import { useLocalization } from "../hooks/useLocalization";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [resetToken, setResetToken] = useState(""); // For development only
  
  const navigate = useNavigate();
  const { t, isEnglish } = useLocalization();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(`${API}/forgot-password`, { email }, {
        headers: {
          'Accept-Language': isEnglish ? 'en-US,en;q=0.9' : 'es-ES,es;q=0.9'
        }
      });
      setMessage(response.data.message);
      
      // For development - show reset token (remove in production)
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('forgotPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTestReset = () => {
    // For development - navigate to reset page with token
    if (resetToken) {
      navigate(`/reset-password?token=${resetToken}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate("/user-login")}
          className="mb-8 flex items-center text-white hover:text-purple-200 transition-colors duration-300"
        >
          <ArrowLeft size={20} className="mr-2" />
          {t('backToLogin')}
        </button>

        {/* Forgot Password Form */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <Mail size={32} className="text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {t('forgotPasswordTitle')}
            </h1>
            <p className="text-gray-600">
              {t('forgotPasswordDescription')}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <p>{message}</p>
              {resetToken && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    {t('developmentMode')}
                  </p>
                  <code className="text-xs bg-gray-100 p-1 rounded block mb-2 break-all">
                    {resetToken}
                  </code>
                  <button
                    onClick={handleTestReset}
                    className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700 transition-colors duration-300"
                  >
                    {t('testReset')}
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('email')}
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                  disabled={loading}
                  placeholder={t('emailPlaceholder')}
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('sending')}
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  {t('sendRecoveryLink')}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-4">
            <p className="text-sm text-gray-600">
              {t('rememberedPassword')}{" "}
              <Link
                to="/user-login"
                className="font-medium text-purple-600 hover:text-purple-500 transition-colors duration-300"
              >
                {t('login')}
              </Link>
            </p>
            
            <p className="text-sm text-gray-600">
              {t('noAccount')}{" "}
              <Link
                to="/register"
                className="font-medium text-purple-600 hover:text-purple-500 transition-colors duration-300"
              >
                {t('createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;